// ============================================================================
// MULTI-SOURCE SUGGESTIONS ENGINE
//
// Single source of truth for the contextual-suggestions block that lives in
// the right-hand Properties panel across every module. Returns four parallel
// lists keyed by source — Library, ISO 14224, SFG20, OptimalAI — so the UI
// can render them as four stacked sections with consistent behaviour.
//
// Philosophy
//   1. Every module renders the same MultiSourceSuggestions component in the
//      same place (Properties panel). Module differences are reflected by
//      sectionId and the active project scope, never by separate panels.
//   2. Each source returns suggestions in a uniform shape:
//        { id, description, severity?, mechanism?, cause?, interval?,
//          intervalUnit?, source, sourceLabel, _aiBoost?, _aiReason? }
//   3. OptimalAI is a deterministic ranker layered on the other three plus
//      four context layers: sibling-aware, parent-aware, child roll-up,
//      project-history-aware.
//   4. No external API calls; everything works offline with the data tree
//      shipped under /data/ and /sfg20/.
// ============================================================================

import {
  searchLibrary as libSearch,
  loadEquipmentTemplate as libLoadTemplate,
  applyTemplateToProject as libApplyTemplate,
} from './failureLibraryEngine.js';
import {
  TYPE_ALIASES,
  resolveEquipmentType,
  getSmartSuggestions as taxonomySmartSuggestions,
} from './equipmentTaxonomy.js';

export const SOURCE_ORDER = ['library', 'iso', 'sfg20', 'ai'];
export const SOURCE_LABELS = {
  library: 'Library',
  iso: 'ISO 14224',
  sfg20: 'SFG20',
  ai: 'OptimalAI',
};

// ---------------------------------------------------------------------------
// SFG20 (PM schedules) — lazy-loaded once per session
// ---------------------------------------------------------------------------
let _sfg20Index = null;
let _sfg20Schedules = null;
let _sfg20LoadingPromise = null;

async function loadSFG20() {
  if (_sfg20Index && _sfg20Schedules) {
    return { index: _sfg20Index, schedules: _sfg20Schedules };
  }
  if (_sfg20LoadingPromise) return _sfg20LoadingPromise;

  _sfg20LoadingPromise = (async () => {
    try {
      const [iRes, sRes] = await Promise.all([
        fetch('./sfg20/index.json'),
        fetch('./sfg20/schedules.json'),
      ]);
      const i = iRes.ok ? await iRes.json() : null;
      const s = sRes.ok ? await sRes.json() : null;
      _sfg20Index = i;
      _sfg20Schedules = s;
      _sfg20LoadingPromise = null;
      return { index: i, schedules: s };
    } catch (err) {
      console.warn('[multiSourceSuggestions] SFG20 load failed:', err);
      _sfg20LoadingPromise = null;
      return { index: null, schedules: null };
    }
  })();
  return _sfg20LoadingPromise;
}

// ---------------------------------------------------------------------------
// Section to relevance map — which sources are meaningful for which section.
// Used by the UI to suppress irrelevant source sections (e.g., SFG20 has no
// failure-mode data; Library has no functional descriptions).
// ---------------------------------------------------------------------------
export const SECTION_SOURCE_RELEVANCE = {
  rcmScope:         { library: false, iso: false, sfg20: false, ai: false },
  rcmFunctions:    { library: false, iso: true,  sfg20: false, ai: true },
  functions:        { library: true,  iso: true,  sfg20: false, ai: true },
  failuremodes:     { library: true,  iso: true,  sfg20: false, ai: true },
  failuremodels:    { library: true,  iso: true,  sfg20: false, ai: true },
  rcmcauses:        { library: true,  iso: true,  sfg20: false, ai: true },
  consequences:     { library: false, iso: true,  sfg20: false, ai: true },
  scheduledtasks:   { library: true,  iso: true,  sfg20: true,  ai: true },
  correctivetasks:  { library: true,  iso: true,  sfg20: false, ai: true },
  spares:           { library: true,  iso: true,  sfg20: false, ai: true },
};

export function isSourceRelevant(sectionId, source) {
  return SECTION_SOURCE_RELEVANCE[sectionId]?.[source] ?? true;
}

export function resolveSuggestionEquipmentType(equipmentType) {
  const raw = String(equipmentType || '').trim();
  if (!raw) return { requestedType: '', resolvedType: '', matchLevel: 'none', fallbackNote: '' };

  const exact = resolveEquipmentType(raw);
  if (exact && exact !== '_Generic') {
    return { requestedType: raw, resolvedType: exact, matchLevel: exact.toLowerCase() === raw.toLowerCase() ? 'exact' : 'controlled-parent', fallbackNote: exact.toLowerCase() === raw.toLowerCase() ? '' : `Using controlled parent class "${exact}" for "${raw}".` };
  }

  const lower = raw.toLowerCase();
  const alias = TYPE_ALIASES[lower] || Object.entries(TYPE_ALIASES).find(([key]) => lower.includes(key))?.[1];
  if (alias) {
    return { requestedType: raw, resolvedType: alias, matchLevel: 'controlled-parent', fallbackNote: `Using controlled parent class "${alias}" for "${raw}".` };
  }

  return { requestedType: raw, resolvedType: '', matchLevel: 'none', fallbackNote: '' };
}

// ---------------------------------------------------------------------------
// Project context — sibling, parent, roll-up, project-history
// ---------------------------------------------------------------------------
export function computeProjectContext(selectedItem, projectData) {
  const allEquipments  = projectData?.equipments || [];
  const allLocations   = projectData?.locations || [];
  const allFailures    = projectData?.failuremodels || projectData?.failuremodes || [];
  const allTasks       = [
    ...(projectData?.scheduledtasks || []),
    ...(projectData?.correctivetasks || []),
  ];

  // Resolve the selected equipment record (if applicable)
  let selectedEquip = null;
  if (selectedItem?.id) {
    selectedEquip = allEquipments.find(e => e.id === selectedItem.id) || null;
    if (!selectedEquip && selectedItem?.equipmentId) {
      selectedEquip = allEquipments.find(e => e.id === selectedItem.equipmentId) || null;
    }
  }

  // Siblings — same equipment type, same parent
  const siblings = selectedEquip
    ? allEquipments.filter(e =>
        e.id !== selectedEquip.id &&
        (e.parentId || '') === (selectedEquip.parentId || '') &&
        (e.type || '').toLowerCase() === (selectedEquip.type || '').toLowerCase())
    : [];

  // Parent record
  const parentKey = selectedEquip?.parentId || selectedEquip?.parent || selectedEquip?.location || selectedItem?.parentId || selectedItem?.parent || selectedItem?.location || '';
  const parent = parentKey
    ? (allEquipments.find(e => e.id === parentKey)
       || allLocations.find(l => l.id === parentKey))
    : null;

  // Children — for roll-up when a system/location is selected
  const children = selectedItem?.id
    ? allEquipments.filter(e => e.parentId === selectedItem.id || e.parent === selectedItem.id || e.location === selectedItem.id)
    : [];

  const hierarchyPath = [];
  let cursor = parent;
  const guard = new Set();
  while (cursor && cursor.id && !guard.has(cursor.id) && hierarchyPath.length < 12) {
    guard.add(cursor.id);
    hierarchyPath.unshift(cursor);
    const nextKey = cursor.parentId || cursor.parent || cursor.location || '';
    cursor = nextKey
      ? (allEquipments.find(e => e.id === nextKey) || allLocations.find(l => l.id === nextKey))
      : null;
  }

  // Project failure-mode frequency — descriptor stem -> count
  const failureFrequency = {};
  allFailures.forEach(f => {
    const key = stemKey(f.description || f.mechanism || '');
    if (key) failureFrequency[key] = (failureFrequency[key] || 0) + 1;
  });

  return {
    selectedItem,
    selectedEquip,
    siblings,
    parent,
    children,
    hierarchyPath,
    allEquipments,
    allFailures,
    allTasks,
    failureFrequency,
  };
}

export function buildSuggestionGate({
  workflowId,
  workflowStepId,
  sectionId,
  tickedItems = [],
  projectData,
}) {
  const explicitItems = (tickedItems || []).filter(Boolean);
  if (!workflowId || !workflowStepId || !sectionId || explicitItems.length === 0) {
    return {
      allowed: false,
      reason: 'Suggestions require current workflow, current step, and at least one explicitly ticked item.',
      items: [],
    };
  }

  return {
    allowed: true,
    reason: '',
    workflowId,
    workflowStepId,
    sectionId,
    items: explicitItems.map(item => ({
      item,
      context: computeProjectContext(item, projectData),
    })),
  };
}

function stemKey(text) {
  return String(text || '').toLowerCase().trim().split(/\s+/).slice(0, 3).join(' ');
}

// ---------------------------------------------------------------------------
// Source 1: Library (Optimal Asset Maintenance Reference Library, 768 types)
// ---------------------------------------------------------------------------
export async function getLibrarySuggestions(equipmentType, sectionId, ctx) {
  if (!equipmentType || !isSourceRelevant(sectionId, 'library')) return [];
  try {
    const matches = await libSearch(equipmentType, 1);
    if (!matches || matches.length === 0) return [];
    const top = matches[0];
    if (!top.fileName) return [];
    const tpl = await libLoadTemplate(top.fileName);
    if (!tpl) return [];

    // applyTemplateToProject yields { failuremodes, scheduledtasks, correctivetasks }.
    // We extract the section we need.
    const applied = libApplyTemplate(tpl, ctx?.selectedEquip?.id || '', equipmentType, {
      includeFailureModes: true,
      includeTasks: true,
      prefix: 'LIB-',
    });
    const sectionMap = {
      functions:       [],
      rcmFunctions:    [],
      failuremodes:    applied.failuremodes,
      failuremodels:   applied.failuremodes,
      rcmcauses:       applied.failuremodes,
      consequences:    [],
      scheduledtasks:  applied.scheduledtasks,
      correctivetasks: applied.correctivetasks,
      spares:          [],
    };
    const items = sectionMap[sectionId] || [];
    return items.slice(0, 12).map(it => ({
      ...it,
      source:      'Library: ' + (top.equipmentType || equipmentType),
      sourceLabel: 'Library',
    }));
  } catch (err) {
    console.warn('[multiSourceSuggestions] Library lookup failed:', err);
    return [];
  }
}

// ---------------------------------------------------------------------------
// Source 2: ISO 14224 (taxonomy module already in the engine)
// ---------------------------------------------------------------------------
export function getISO14224Suggestions(equipmentType, sectionId, ctx) {
  if (!equipmentType || !isSourceRelevant(sectionId, 'iso')) return [];
  try {
    const items = taxonomySmartSuggestions(
      equipmentType,
      sectionId,
      ctx?.selectedEquip?.id || '',
      [],
      { selectedFunction: null, selectedFailureMode: null }
    ) || [];
    return items.slice(0, 12).map(it => ({
      ...it,
      source:      it.source || 'ISO 14224:2016',
      sourceLabel: 'ISO 14224',
    }));
  } catch (err) {
    console.warn('[multiSourceSuggestions] ISO 14224 lookup failed:', err);
    return [];
  }
}

// ---------------------------------------------------------------------------
// Source 3: SFG20 (PM schedules)
// ---------------------------------------------------------------------------
export async function getSFG20Suggestions(equipmentType, sectionId, ctx) {
  if (!equipmentType || !isSourceRelevant(sectionId, 'sfg20')) return [];
  const { index, schedules } = await loadSFG20();
  if (!index || !schedules) return [];

  const t = String(equipmentType).toLowerCase();
  // The SFG20 index typically maps equipment categories to schedule refs.
  // We do a simple substring match on schedule title and tags.
  const matched = (schedules || []).filter(s => {
    const haystack = ((s.title || '') + ' ' + (s.equipment || '') + ' ' + (s.tags || []).join(' ')).toLowerCase();
    return haystack.includes(t) || (TYPE_ALIASES[t] && haystack.includes(TYPE_ALIASES[t].toLowerCase()));
  });

  return matched.slice(0, 12).flatMap(sched => {
    const tasks = (sched.tasks || []).slice(0, 3);
    return tasks.map((task, i) => ({
      id:           `SFG-${sched.ref}-${i}`,
      description:  `[${sched.ref}] ${task.item || task.description || ''}`,
      action:       task.action || '',
      interval:     task.interval || 0,
      intervalUnit: task.intervalUnit || 'hours',
      frequency:    task.frequency || '',
      strategy:     'Fixed Time',
      source:       `SFG20 ${sched.ref} — ${sched.title}`,
      sourceLabel:  'SFG20',
      sfg20Ref:     sched.ref,
      libraryRef:   `SFG20:${sched.ref}`,
    }));
  });
}

// ---------------------------------------------------------------------------
// Source 4: OptimalAI — deterministic ranker
//
// Combines ISO 14224 + Library, then re-ranks by four context layers:
//   1. Sibling: failure modes already recorded against same-type siblings
//   2. Parent: parent system criticality biases containment / safety modes
//   3. Roll-up: when the user selected a system, child equipment failure
//      modes propagate up
//   4. Project history: descriptors recurring across the project gain weight
//
// Returns up to 8 ranked suggestions tagged with _aiReason for tooltip
// surfacing of WHY this one is up top.
// ---------------------------------------------------------------------------
export async function getOptimalAISuggestions(equipmentType, sectionId, ctx, projectData) {
  if (!equipmentType || !isSourceRelevant(sectionId, 'ai')) return [];

  const [iso, library] = await Promise.all([
    Promise.resolve(getISO14224Suggestions(equipmentType, sectionId, ctx)),
    getLibrarySuggestions(equipmentType, sectionId, ctx),
  ]);
  const candidates = [...iso, ...library];
  if (candidates.length === 0) return [];

  // Pre-compute reusable context buckets
  const siblingFailures = (ctx?.siblings || []).flatMap(s =>
    (projectData?.failuremodels || projectData?.failuremodes || []).filter(f =>
      f.equipmentId === s.id || f.equipment === s.description
    )
  );
  const siblingFailureKeys = new Set(siblingFailures.map(f => stemKey(f.description || f.mechanism || '')));
  const parentCriticality = (ctx?.parent?.criticality || '').toLowerCase();
  const isParentCritical = parentCriticality === 'high' || parentCriticality === 'critical' || parentCriticality === 'a';

  // Score each candidate
  const scored = candidates.map(item => {
    let score = 50;
    const reasons = [];
    const desc = (item.description || '').toLowerCase();
    const mech = (item.mechanism || '').toLowerCase();
    const text = desc + ' ' + mech;

    // 1. Sibling-aware
    const key = stemKey(item.description || item.mechanism || '');
    if (key && siblingFailureKeys.has(key)) {
      score += 25;
      reasons.push('seen on sibling equipment');
    }

    // 2. Parent-aware
    if (isParentCritical && /leak|seal|contain|safety|protection|emission|environment/i.test(text)) {
      score += 18;
      reasons.push('parent system high-criticality favours containment');
    }

    // 3. Project history
    const hist = ctx?.failureFrequency?.[key] || 0;
    if (hist > 1) {
      score += Math.min(hist * 6, 24);
      reasons.push(`${hist}× across project`);
    }

    // 4. Roll-up — if many children of same parent share this failure
    if ((ctx?.children || []).length > 0) {
      score += 5;
      reasons.push('aggregating children');
    }

    // Severity bias — prefer high-severity items
    if (item.severity != null) {
      const sev = Number(item.severity) || 0;
      score += sev;
    }

    return {
      ...item,
      _aiScore:    score,
      _aiReason:   reasons.join('; '),
      source:      'OptimalAI deterministic ranking of ' + (item.sourceLabel || 'source') + ' match',
      sourceLabel: 'OptimalAI',
      _aiDeterministic: true,
    };
  });

  // Dedupe by stemmed description, keep highest score
  const byKey = new Map();
  scored.forEach(item => {
    const key = stemKey(item.description || '');
    const existing = byKey.get(key);
    if (!existing || item._aiScore > existing._aiScore) {
      byKey.set(key, item);
    }
  });

  return Array.from(byKey.values())
    .sort((a, b) => b._aiScore - a._aiScore)
    .slice(0, 8);
}

// ---------------------------------------------------------------------------
// Top-level entry point
// ---------------------------------------------------------------------------
export async function getMultiSourceSuggestions(equipmentType, sectionId, projectContext, projectData) {
  const ctx = projectContext || {};
  const resolution = resolveSuggestionEquipmentType(equipmentType);
  const resolvedType = resolution.resolvedType;
  if (!resolvedType) {
    return {
      library: [],
      iso: [],
      sfg20: [],
      ai: [],
      relevance: SECTION_SOURCE_RELEVANCE[sectionId] || { library: true, iso: true, sfg20: true, ai: true },
      resolution,
      contextSummary: {
        siblingCount: (ctx.siblings || []).length,
        parentName: ctx.parent?.description || ctx.parent?.name || null,
        parentCriticality: ctx.parent?.criticality || null,
        childCount: (ctx.children || []).length,
        historyHits: Object.keys(ctx.failureFrequency || {}).length,
      },
    };
  }

  const [library, iso, sfg20, ai] = await Promise.all([
    getLibrarySuggestions(resolvedType, sectionId, ctx),
    Promise.resolve(getISO14224Suggestions(resolvedType, sectionId, ctx)),
    getSFG20Suggestions(resolvedType, sectionId, ctx),
    getOptimalAISuggestions(resolvedType, sectionId, ctx, projectData || {}),
  ]);

  // Build a relevance map so the UI can suppress empty / N/A sources
  const relevance = SECTION_SOURCE_RELEVANCE[sectionId] || { library: true, iso: true, sfg20: true, ai: true };

  return {
    library: relevance.library ? library : [],
    iso:     relevance.iso     ? iso     : [],
    sfg20:   relevance.sfg20   ? sfg20   : [],
    ai:      relevance.ai      ? ai      : [],
    relevance,
    resolution,
    contextSummary: {
      siblingCount:    (ctx.siblings || []).length,
      parentName:      ctx.parent?.description || ctx.parent?.name || null,
      parentCriticality: ctx.parent?.criticality || null,
      childCount:      (ctx.children || []).length,
      hierarchyPath:   (ctx.hierarchyPath || []).map(n => n.description || n.name || n.id),
      historyHits:     Object.keys(ctx.failureFrequency || {}).length,
    },
  };
}
